/**
 * Onboarding — smart entry point after Google OAuth login.
 *
 * Checks billing status and renders the right UI:
 *   • No plan yet     → "Start Free Trial" (activate + go to dashboard)
 *   • Trial active    → redirect to /dashboard immediately
 *   • Trial expired   → "Trial ended" + paid plan cards + QR payment flow
 *   • Pro/Team/Biz    → redirect to /dashboard immediately
 */
import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Bot, Sparkles, ArrowRight, Check, Loader2, Zap, Shield,
  Play, BarChart3, GitBranch, Clock, MessageCircle, Building2, Users
} from 'lucide-react';
import { toast } from 'sonner';
import PaymentQRModal from '@/components/PaymentQRModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FEATURES = [
  { icon: Sparkles, label: 'AI Test Case Generation',  desc: 'Generate tests from user stories & screenshots' },
  { icon: Play,     label: 'Unlimited Test Runs',       desc: 'Execute UI & API tests with Playwright' },
  { icon: GitBranch,label: 'API Test Import',           desc: 'Import cURL, Postman collections & auto-generate tests' },
  { icon: BarChart3,label: 'Analytics & Reports',       desc: 'Visual regression, execution history, trends' },
  { icon: Shield,   label: 'Self-Healing Tests',        desc: 'AI auto-fixes broken selectors across deployments' },
];

const PAID_PLANS = [
  {
    id: 'pro',
    name: 'Pro',
    price: '₹2,500',
    icon: Zap,
    color: '#00E599',
    features: ['Unlimited executions', 'AI test generation', 'API testing', 'Priority support'],
  },
  {
    id: 'team',
    name: 'Team',
    price: '₹6,500',
    icon: Users,
    color: '#D946EF',
    features: ['Everything in Pro', 'Up to 10 members', 'Team collaboration', 'Shared dashboards'],
  },
  {
    id: 'business',
    name: 'Business',
    price: '₹16,000',
    icon: Building2,
    color: '#F59E0B',
    features: ['Everything in Team', 'Unlimited members', 'Dedicated support', 'Custom integrations'],
  },
];

export default function Onboarding() {
  const navigate   = useNavigate();
  const location   = useLocation();
  const { user, refreshUser } = useAuth();

  const [planStatus,  setPlanStatus]  = useState(null);
  const [planLoading, setPlanLoading] = useState(true);
  const [activating,  setActivating]  = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null); // plan id for QR modal

  const userData = location.state?.user || user;

  /* ── Fetch billing status ── */
  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/billing/status`, { withCredentials: true })
      .then(res => {
        const s = res.data;
        setPlanStatus(s);
        // Auto-redirect if already has active plan
        if (s.has_plan && s.status === 'active') {
          navigate('/dashboard', { replace: true });
        }
      })
      .catch(() => setPlanStatus(null))
      .finally(() => setPlanLoading(false));
  }, [user, navigate]);

  /* ── Activate trial ── */
  const handleActivateTrial = async () => {
    setActivating(true);
    try {
      await axios.post(`${API}/billing/activate-trial`, {}, { withCredentials: true });
      toast.success('7-day free trial activated! Welcome to AutoQA.ai 🎉');
      await refreshUser();
      navigate('/dashboard', { replace: true });
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to activate trial. Please try again.';
      toast.error(typeof msg === 'object' ? msg.message : msg);
    } finally {
      setActivating(false);
    }
  };

  /* ── Loading state ── */
  if (planLoading) {
    return (
      <div className="min-h-screen bg-[#030712] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 bg-[#00E599] rounded-lg flex items-center justify-center">
            <Bot className="w-7 h-7 text-black" />
          </div>
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      </div>
    );
  }

  const isExpired = planStatus?.status === 'expired' || planStatus?.status === 'cancelled';

  return (
    <div className="min-h-screen bg-[#030712] text-white">
      <div className="fixed inset-0 hero-glow" />

      <div className="relative z-10 min-h-screen flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-2xl"
        >
          {/* Logo + greeting */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-2 mb-4">
              <div className="w-12 h-12 bg-[#00E599] rounded-lg flex items-center justify-center">
                <Bot className="w-7 h-7 text-black" />
              </div>
              <h1 className="text-3xl font-bold" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                AutoQA.ai
              </h1>
            </div>
            <h2 className="text-2xl font-semibold mb-2">
              Welcome{userData?.name ? `, ${userData.name.split(' ')[0]}` : ''}! 👋
            </h2>
            <p className="text-slate-400">
              {isExpired
                ? 'Your free trial has ended — choose a plan to continue.'
                : "You're one click away from AI-powered test automation"}
            </p>
          </div>

          {/* ── EXPIRED: show paid plan cards ── */}
          {isExpired ? (
            <>
              {/* Trial expired notice */}
              <div className="flex items-center gap-3 bg-orange-500/10 border border-orange-500/30 rounded-xl px-4 py-3 mb-6">
                <Clock className="w-5 h-5 text-orange-400 flex-shrink-0" />
                <p className="text-sm text-orange-300">
                  Your 7-day free trial has expired. Select a plan below to keep using AutoQA.ai.
                </p>
              </div>

              {/* Paid plan cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                {PAID_PLANS.map(plan => {
                  const Icon = plan.icon;
                  return (
                    <div
                      key={plan.id}
                      className="bg-[#0D1117] border border-white/10 hover:border-white/20 rounded-2xl p-5 flex flex-col transition-all cursor-pointer"
                      onClick={() => setSelectedPlan(plan.id)}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <Icon className="w-4 h-4" style={{ color: plan.color }} />
                        <span className="font-semibold text-white text-sm">{plan.name}</span>
                      </div>
                      <p className="text-xl font-bold text-white mb-3">
                        {plan.price}<span className="text-xs font-normal text-slate-400">/mo</span>
                      </p>
                      <ul className="space-y-1.5 flex-1 mb-4">
                        {plan.features.map(f => (
                          <li key={f} className="flex items-start gap-2 text-[11px] text-slate-300">
                            <Check className="w-3 h-3 text-[#00E599] flex-shrink-0 mt-0.5" />
                            {f}
                          </li>
                        ))}
                      </ul>
                      <Button
                        className="w-full h-9 text-xs font-semibold bg-white/5 border border-white/10 hover:bg-white/10 text-white"
                        onClick={() => setSelectedPlan(plan.id)}
                      >
                        <MessageCircle className="w-3 h-3 mr-1.5" style={{ color: plan.color }} />
                        Get Started
                      </Button>
                    </div>
                  );
                })}
              </div>

              <p className="text-center text-xs text-slate-600">
                Pay via UPI · Ashish activates your plan within minutes after receiving payment proof
              </p>
            </>
          ) : (
            /* ── NO PLAN: show free trial card ── */
            <>
              <div className="bg-gradient-to-br from-[#00E599]/5 to-transparent border border-[#00E599]/30 rounded-2xl p-6 mb-6">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Zap className="w-5 h-5 text-[#00E599]" />
                    <span className="font-bold text-white" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                      Free Plan
                    </span>
                  </div>
                  <Badge className="bg-[#00E599]/15 text-[#00E599] border-[#00E599]/30 font-semibold text-xs">
                    No credit card required
                  </Badge>
                </div>
                <p className="text-slate-300 text-sm mb-4">
                  Get <span className="text-[#00E599] font-semibold">5 AI test generations</span> and{' '}
                  <span className="text-[#00E599] font-semibold">5 test runs</span> free every month —
                  full access to every feature.
                </p>
                <div className="space-y-2.5">
                  {FEATURES.map(({ icon: Icon, label, desc }) => (
                    <div key={label} className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-[#00E599]/10 border border-[#00E599]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="w-3 h-3 text-[#00E599]" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-white">{label}</p>
                        <p className="text-xs text-slate-400">{desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <Button
                className="w-full h-12 bg-[#00E599] text-black hover:bg-[#00CC88] font-bold text-base mb-3"
                onClick={handleActivateTrial}
                disabled={activating}
                data-testid="activate-trial-btn"
              >
                {activating ? (
                  <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Activating…</>
                ) : (
                  <><Sparkles className="w-5 h-5 mr-2" /> Start Free <ArrowRight className="w-5 h-5 ml-2" /></>
                )}
              </Button>

              <p className="text-center text-xs text-slate-500">
                5 free generations + 5 free runs each month. Upgrade anytime for unlimited usage.
              </p>
            </>
          )}
        </motion.div>
      </div>

      {/* QR Payment Modal */}
      <PaymentQRModal
        open={!!selectedPlan}
        plan={selectedPlan}
        onClose={() => setSelectedPlan(null)}
      />
    </div>
  );
}
